import React, { useEffect, useState } from 'react';

import {
    Paper,
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableHead,
    TableCell,
    TableContainer,
    TableRow,
    LinearProgress,
    Alert,
} from '@mui/material';

import MapIcon from '@mui/icons-material/Map';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UndoIcon from '@mui/icons-material/Undo';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { useAppSelector, useAppDispatch } from 'hooks';
import { BFMCManager, WorkflowManager } from 'models';
import { GuideActions } from 'state';
import { toTimeDDMonYearFormat, hasRole } from 'utils';
import {
    BfWorkflowEnum,
    BfWorkshopId,
    FaftWorkflowEnum,
    FaftWorkshopId,
    BfWorkflowId,
    FaftWorkflowId,
    endorsedIds,
} from 'models/workflow';
import PlanErrorSegment from './PlanErrorSegment';
import NewPlanDialog from './NewPlanDialog';
import UpdatePlanDialog from './UpdatePlanDialog';
import {
    FAFT_PRIORITISATION_TASK_STATUS,
    FEATURE,
    isFeatureEnabled,
} from 'config';
import { FaftPrioritisationTask } from 'models/faftprioritisation';
import { FaftPrioritisationActions } from 'state/faftprioritisation';

import { nextStageAllowed } from 'models/permissions';
import {
    getBmcDisplay,
    getStatusPalette,
    getWorkshopProgress,
} from 'utils/bmcDisplay';

interface Props {
    onClose: () => void;
    onMapClick: () => void;
    bfmc: BFMCManager.BFMC;
    loading: boolean;
}

// Shared style for the figma-styled action-bar buttons
const actionButtonSx = {
    color: '#666',
    textTransform: 'uppercase' as const,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    px: 2,
    py: 1,
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.2s ease',
    '&:hover': {
        color: '#374151',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        transform: 'translateY(-2px)',
    },
};

const BFMCDetails: React.FunctionComponent<Props> = ({
    onClose,
    bfmc,
    onMapClick,
    loading,
}) => {
    const dispatch = useAppDispatch();
    const [newPlanDialogOpen, setNewPlanDialogOpen] = useState(false);
    const [updatePlanDialogOpen, setUpdatePlanDialogOpen] = useState(false);
    const [stepChangeExecuting, setStepChangeExecuting] = useState(false);
    const [userInfo, setUserInfo] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const {
        auth: { user },
        guide,
        faftprioritisation,
    } = useAppSelector((s) => s);

    const group = BFMCManager.getBFMCGroupFromBFMC(
        bfmc,
        guide.BFMCGroups.object,
    );

    const plans = bfmc.plans.filter(
        (plan) => plan.plan_type === guide.selectedPlanType,
    );

    const currentPlan = plans.length === 1 ? plans[0] : undefined;
    if (!currentPlan) {
        setErrorMessage(
            `No active plan of type ${guide.selectedPlanType} was found for the BMC`,
        );
    }

    const workshops =
        guide.selectedPlanType === 'bfrmp'
            ? WorkflowManager.bfWorkshopIds.map(
                  (id) => WorkflowManager.bfWorkshops[id],
              )
            : WorkflowManager.faftWorkshopIds.map(
                  (id) => WorkflowManager.faftWorkshops[id],
              );

    const workflowNotStarted =
        guide.selectedPlanType === 'bfrmp'
            ? 'bf_not_started'
            : 'faft_not_started';

    const stage = currentPlan
        ? WorkflowManager.getWorkflow(
              guide.selectedPlanType,
              currentPlan.workshop_status,
          )
        : WorkflowManager.getWorkflow(
              guide.selectedPlanType,
              workflowNotStarted,
          );

    const currentWorkshop =
        guide.selectedPlanType === 'bfrmp'
            ? WorkflowManager.bfWorkshops[stage.workshopId as BfWorkshopId]
            : WorkflowManager.faftWorkshops[stage.workshopId as FaftWorkshopId];

    const transitions = currentPlan ? currentPlan.updates : [];
    const isAdmin = hasRole(user.object, 'Project Manager', 'Project Admin');
    const isSuperAdmin = hasRole(user.object, 'Project Manager');

    const roles = user.object?.roles ?? [''];
    const bfmcStatus = guide.selectedPlan?.workshop_status ?? '';
    const nextStageButtonEnable = nextStageAllowed(bfmcStatus, roles);

    const planReadyForApproval = stage.id == 'bf_workshop_4_processing';
    const planSentForApproval =
        stage.id == 'bf_workshop_4_completed' ||
        stage.id == 'faft_workshop_4_completed';
    const planApproved =
        stage.id == 'bf_workshop_4_approved' ||
        stage.id == 'faft_workshop_4_approved';

    // If plan is approved or sent for approval, only super admin can undo workflow status
    const canUndoWorkflowStatus = () => {
        if (planSentForApproval || planApproved) {
            return isSuperAdmin;
        }
        return isAdmin;
    };

    const faftPrioritisationTask: FaftPrioritisationTask | null =
        faftprioritisation.faftPrioritisationTask.object || null;
    const faftPrioritisationTaskFetchStatus: string =
        faftprioritisation.faftPrioritisationTask.status;

    useEffect(() => {
        const { selectedBFMC, selectedPlanType } = guide;
        if (isAdmin && selectedBFMC && selectedPlanType === 'faft') {
            const planId = selectedBFMC.faft_plan_id;
            dispatch(
                FaftPrioritisationActions.getFaftPrioritisationTask({
                    planId,
                }),
            );
            const intervalId = setInterval(async () => {
                dispatch(
                    FaftPrioritisationActions.getFaftPrioritisationTask({
                        planId,
                    }),
                );
            }, 60000);
            return () => clearInterval(intervalId);
        }
    }, [isAdmin, guide.selectedBFMC, guide.selectedPlanType]);

    const getPrioritisationTaskAlertSeverity = (
        status: string,
        errorCount: number,
    ) => {
        switch (status) {
            case FAFT_PRIORITISATION_TASK_STATUS.PENDING:
            case FAFT_PRIORITISATION_TASK_STATUS.STARTED:
                return 'info';
            case FAFT_PRIORITISATION_TASK_STATUS.CANCELLED:
                return 'warning';
            case FAFT_PRIORITISATION_TASK_STATUS.FAILED:
                return 'error';
            case FAFT_PRIORITISATION_TASK_STATUS.COMPLETED:
                if (errorCount && errorCount > 0) return 'warning';
                return 'success';
            default:
                return 'info';
        }
    };

    const generatePrioritisationTaskMessage = (
        id: number,
        status: string,
        errorCount: number,
    ): string => {
        switch (status) {
            case FAFT_PRIORITISATION_TASK_STATUS.PENDING:
            case FAFT_PRIORITISATION_TASK_STATUS.STARTED:
                return `Please wait for processing to complete (task #${id}). To cancel and return to workshop 1, please choose "Undo Step".`;
            case FAFT_PRIORITISATION_TASK_STATUS.CANCELLED:
                return `The FAFT prioritisation processing (task #${id}) has been cancelled.`;
            case FAFT_PRIORITISATION_TASK_STATUS.FAILED:
                return `The FAFT prioritisation processing (task #${id}) failed with errors. Please contact support to investigate the issue further.`;
            case FAFT_PRIORITISATION_TASK_STATUS.COMPLETED:
                return `The FAFT prioritisation processing (task #${id}) has been completed ${
                    errorCount > 0 ? 'with some errors.' : 'successfully.'
                }`;
            default:
                return 'info';
        }
    };

    const isProceedToNextStageDisabled = (
        actionType: string,
        workshopId: string | undefined,
        stepId:
            | (keyof typeof FaftWorkflowEnum | keyof typeof BfWorkflowEnum)
            | undefined,
    ) => {
        if (stepChangeExecuting) return true;

        if (planReadyForApproval && !nextStageButtonEnable) {
            return true;
        }

        if (actionType == 'CommenceWorkshop') {
            if (stage.next?.workshopId !== workshopId) return true;
            if (
                faftPrioritisationTask &&
                faftPrioritisationTaskFetchStatus &&
                guide.selectedPlanType === 'faft'
            ) {
                if (faftPrioritisationTaskFetchStatus !== 'finished')
                    return true;
                const { status } = faftPrioritisationTask;
                if (
                    [
                        FAFT_PRIORITISATION_TASK_STATUS.PENDING,
                        FAFT_PRIORITISATION_TASK_STATUS.STARTED,
                    ].includes(status)
                ) {
                    return true;
                }
            }
            return false;
        } else {
            return (
                guide.BFMCGroups.status === 'loading' ||
                (!isFeatureEnabled(FEATURE.FAFT) &&
                    stepId != null &&
                    endorsedIds.includes(stepId))
            );
        }
    };

    const setNextStage = async () => {
        if (!stage?.next) return;
        setErrorMessage('');
        if (guide.BFMCGroups.status === 'loading') return;
        setStepChangeExecuting(true);

        if (stage.scope === 'group' && group) {
            await dispatch(
                GuideActions.sendUpdateStatus({
                    bfmcs: group.bmcs,
                    plan_type: 'bfrmp',
                    status: stage.next.id,
                }),
            ).catch((err) => {
                if (err && err.data)
                    setErrorMessage(
                        err.data?.detail?.msg ??
                            'An unexpected error occurred.',
                    );
            });
        } else {
            await dispatch(
                GuideActions.sendUpdateStatus({
                    bfmcs: [bfmc.id],
                    plan_type: guide.selectedPlanType,
                    status: stage.next.id,
                }),
            ).catch((err) => {
                if (err && err.data)
                    setErrorMessage(
                        err.data?.detail?.msg ??
                            'An unexpected error occurred.',
                    );
            });
        }
        await dispatch(GuideActions.getBFMCs());
        setStepChangeExecuting(false);
    };

    const onUndoStepClick = async () => {
        if (!stage?.previous) return;
        setStepChangeExecuting(true);
        setErrorMessage('');

        if (stage.scope === 'group' && group) {
            await dispatch(
                GuideActions.sendUpdateStatus({
                    bfmcs: group.bmcs,
                    plan_type: guide.selectedPlanType,
                    status: stage.previous.id,
                }),
            ).catch((err) => {
                if (err && err.data)
                    setErrorMessage(
                        err.data?.detail?.msg ??
                            'An unexpected error occurred.',
                    );
            });
        } else {
            await dispatch(
                GuideActions.sendUpdateStatus({
                    bfmcs: [bfmc.id],
                    plan_type: guide.selectedPlanType,
                    status: stage.previous.id,
                }),
            ).catch((err) => {
                if (err && err.data)
                    setErrorMessage(
                        err.data?.detail?.msg ??
                            'An unexpected error occurred.',
                    );
            });
        }
        await dispatch(GuideActions.getBFMCs());
        setStepChangeExecuting(false);
    };

    const onNewPlanClick = async () => {
        setErrorMessage('');
        setNewPlanDialogOpen(true);
    };

    const handleCloseNewPlanDialog = () => setNewPlanDialogOpen(false);

    const handleNewPlanCreation = async (confirmed: boolean) => {
        await dispatch(
            GuideActions.sendAddPlan({
                bfmc_id: bfmc.id,
                plan_type: guide.selectedPlanType,
                retain_existing: confirmed,
            }),
        ).catch((err) => {
            if (err && err.data)
                setErrorMessage(
                    err.data?.detail?.msg ?? 'An unexpected error occurred.',
                );
        });
        await dispatch(GuideActions.getBFMCs());
    };

    const onUpdatePlanClick = async () => {
        setErrorMessage('');
        setUpdatePlanDialogOpen(true);
    };

    const handleCloseUpdatePlanDialog = async (reload: boolean) => {
        setUpdatePlanDialogOpen(false);
        if (reload) {
            await dispatch(GuideActions.getBFMCs());
        }
    };

    const handleUpdatePlan = async (
        bfmcId: number,
        planId: string,
        planCode: string,
        planName: string,
    ) => {
        setErrorMessage('');
        await dispatch(
            GuideActions.updatePlanDetails({
                bfmc_id: bfmcId,
                plan_id: planId,
                code: planCode,
                name: planName,
            }),
        ).catch((err) => {
            if (err && err.data)
                setErrorMessage(
                    err.data?.detail?.msg ?? 'An unexpected error occurred.',
                );
        });
    };

    const display = getBmcDisplay(bfmc.code);
    const planName = currentPlan?.name ?? '—';

    return (
        <>
            <Box
                sx={{
                    minHeight: '100%',
                    backgroundColor: '#fafafa',
                    mx: -1,
                    my: -1,
                }}
            >
                {/* Region header banner */}
                <Box sx={{ position: 'relative' }}>
                    {/* Accent stripe */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            backgroundColor: display.accentColour,
                        }}
                    />
                    <Box
                        sx={{
                            backgroundColor: 'white',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
                        }}
                    >
                        <Box
                            sx={{
                                maxWidth: 1280,
                                mx: 'auto',
                                px: { xs: 2, sm: 3 },
                                py: 2,
                                pt: 2.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: {
                                            xs: '1.25rem',
                                            sm: '1.5rem',
                                        },
                                        color: 'text.primary',
                                        fontWeight: 700,
                                    }}
                                >
                                    {bfmc.name}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                        }}
                                    >
                                        Plan:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'text.primary' }}
                                    >
                                        {planName}
                                    </Typography>
                                </Box>
                            </Box>
                            {display.logoSrc && (
                                <Box
                                    component="img"
                                    src={display.logoSrc}
                                    alt={bfmc.name}
                                    sx={{
                                        height: { xs: 56, sm: 72, md: 80 },
                                        width: 'auto',
                                        objectFit: 'contain',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Content with optional region artwork background */}
                <Box sx={{ position: 'relative', py: 4, px: { xs: 2, sm: 3 } }}>
                    {display.backgroundArt && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                backgroundImage: `url(${display.backgroundArt})`,
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'contain',
                                opacity: 0.6,
                                filter: 'saturate(1.3) brightness(1.1)',
                                pointerEvents: 'none',
                            }}
                        />
                    )}

                    <Box
                        sx={{
                            maxWidth: 1280,
                            mx: 'auto',
                            position: 'relative',
                            zIndex: 10,
                        }}
                    >
                        {/* Action button bar */}
                        <Paper
                            sx={{
                                p: 2.5,
                                mb: 3,
                                border: '4px solid white',
                                backgroundColor: display.backgroundArt
                                    ? 'rgba(255, 255, 255, 0.85)'
                                    : 'white',
                                boxShadow:
                                    '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                                transition: 'box-shadow 0.3s ease',
                                '&:hover': {
                                    boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {user.object &&
                                    user.object.BFMCs.length > 1 && (
                                        <Button
                                            variant="text"
                                            startIcon={<ArrowBackIcon />}
                                            onClick={onClose}
                                            sx={actionButtonSx}
                                        >
                                            Close Details
                                        </Button>
                                    )}
                                {canUndoWorkflowStatus() && (
                                    <Button
                                        variant="text"
                                        startIcon={<UndoIcon />}
                                        disabled={
                                            !isFeatureEnabled(FEATURE.FAFT) &&
                                            !!stage?.previous?.id &&
                                            endorsedIds.includes(
                                                stage.previous.id,
                                            )
                                        }
                                        onClick={() =>
                                            bfmc && onUndoStepClick()
                                        }
                                        sx={actionButtonSx}
                                    >
                                        Undo Step
                                    </Button>
                                )}
                                {isSuperAdmin && (
                                    <Button
                                        variant="text"
                                        startIcon={<RefreshIcon />}
                                        disabled={
                                            guide.BFMCs.status !== 'finished'
                                        }
                                        onClick={() =>
                                            bfmc && onUpdatePlanClick()
                                        }
                                        sx={actionButtonSx}
                                    >
                                        Update Plan
                                    </Button>
                                )}
                                {isSuperAdmin && (
                                    <Button
                                        variant="text"
                                        startIcon={<EditIcon />}
                                        onClick={() => bfmc && onNewPlanClick()}
                                        sx={actionButtonSx}
                                    >
                                        New Plan
                                    </Button>
                                )}
                                <Button
                                    variant="text"
                                    startIcon={<MapIcon />}
                                    onClick={() => bfmc && onMapClick()}
                                    sx={actionButtonSx}
                                >
                                    View Map
                                </Button>
                            </Box>
                        </Paper>

                        {/* Error / info / FAFT banners */}
                        {errorMessage && (
                            <Box sx={{ mb: 2 }}>
                                <PlanErrorSegment
                                    onClose={() => setErrorMessage('')}
                                    errorText={errorMessage}
                                />
                            </Box>
                        )}
                        {userInfo && (
                            <Box sx={{ mb: 2 }}>
                                <Alert
                                    variant="standard"
                                    severity="success"
                                    onClose={() => setUserInfo('')}
                                >
                                    {userInfo}
                                </Alert>
                            </Box>
                        )}
                        {guide.selectedPlanType === 'faft' &&
                            isAdmin &&
                            faftPrioritisationTask && (
                                <Box sx={{ mb: 2 }}>
                                    {faftPrioritisationTaskFetchStatus ===
                                    'finished' ? (
                                        <Alert
                                            key={faftPrioritisationTask.id}
                                            variant="outlined"
                                            severity={getPrioritisationTaskAlertSeverity(
                                                faftPrioritisationTask.status,
                                                faftPrioritisationTask.errorCount,
                                            )}
                                        >
                                            {generatePrioritisationTaskMessage(
                                                faftPrioritisationTask.id,
                                                faftPrioritisationTask.status,
                                                faftPrioritisationTask.errorCount,
                                            )}
                                        </Alert>
                                    ) : (
                                        <Alert
                                            variant="outlined"
                                            severity="info"
                                        >
                                            Refreshing ...
                                        </Alert>
                                    )}
                                </Box>
                            )}

                        {/* Workshops table */}
                        <TableContainer
                            component={Paper}
                            sx={{
                                border: '4px solid white',
                                backgroundColor: display.backgroundArt
                                    ? 'rgba(255, 255, 255, 0.85)'
                                    : 'white',
                                boxShadow:
                                    '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                                transition: 'box-shadow 0.3s ease',
                                overflowX: 'auto',
                                '&:hover': {
                                    boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
                                },
                            }}
                        >
                            <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor:
                                                'rgba(249, 250, 251, 0.6)',
                                            borderBottom:
                                                '2px solid rgba(229, 231, 235, 0.6)',
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                width: '15%',
                                                color: '#374151',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Workshop Name
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: '45%',
                                                color: '#374151',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Summary
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: '20%',
                                                color: '#374151',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Status
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: '10%',
                                                color: '#374151',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Date
                                        </TableCell>
                                        {nextStageButtonEnable && (
                                            <TableCell
                                                sx={{
                                                    width: '10%',
                                                    color: '#374151',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                }}
                                            >
                                                Actions
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentPlan &&
                                        workshops.map((workshop) => {
                                            const workshopStatus =
                                                WorkflowManager.getWorkshopStatus(
                                                    guide.selectedPlanType,
                                                    workshop,
                                                    currentPlan.workshop_status,
                                                );
                                            // getWorkflow(null) falls back to *_not_started,
                                            // which is what makes future-workshop rows
                                            // qualify for the "Commence Workshop" button.
                                            const step =
                                                WorkflowManager.getWorkflow(
                                                    guide.selectedPlanType,
                                                    workshopStatus,
                                                );

                                            const date =
                                                workshopStatus != null
                                                    ? transitions?.find(
                                                          (t) =>
                                                              t.to ===
                                                              workshopStatus,
                                                      )?.date
                                                    : null;

                                            const isCurrentWorkshop =
                                                currentWorkshop.id ===
                                                workshop.id;
                                            const showNextStageButton =
                                                isCurrentWorkshop &&
                                                step.next?.workshopId ===
                                                    workshop.id &&
                                                !planSentForApproval &&
                                                !planApproved &&
                                                nextStageButtonEnable;
                                            const showCommenceButton =
                                                !isCurrentWorkshop &&
                                                (step.id === 'bf_not_started' ||
                                                    step.id ===
                                                        'faft_not_started');

                                            // Per-row progress: use the workshop status as it pertains
                                            // to this row's workshop. If null (this workshop hasn't been
                                            // reached), render as not-started.
                                            const progress = workshopStatus
                                                ? getWorkshopProgress(
                                                      guide.selectedPlanType,
                                                      workshopStatus as
                                                          | BfWorkflowId
                                                          | FaftWorkflowId,
                                                  )
                                                : null;
                                            const palette = progress
                                                ? getStatusPalette(
                                                      progress.statusType,
                                                  )
                                                : getStatusPalette('closed');

                                            return (
                                                <TableRow
                                                    key={workshop.id}
                                                    sx={{
                                                        borderBottom:
                                                            '1px solid rgba(229, 231, 235, 0.5)',
                                                        transition:
                                                            'background-color 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(255, 255, 255, 0.4)',
                                                        },
                                                        '&:last-child': {
                                                            borderBottom:
                                                                'none',
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                color: 'text.primary',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {workshop.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                lineHeight: 1.6,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                Task:{' '}
                                                            </Box>
                                                            {workshop.task}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                lineHeight: 1.6,
                                                            }}
                                                        >
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                Action:{' '}
                                                            </Box>
                                                            {workshop.action}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {loading &&
                                                        stepChangeExecuting ? (
                                                            <CircularProgress
                                                                size={20}
                                                            />
                                                        ) : progress &&
                                                          !progress.isNotStarted ? (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: 1.5,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        flexShrink: 0,
                                                                        pt: 0.5,
                                                                    }}
                                                                >
                                                                    {progress.statusType ===
                                                                    'finalised' ? (
                                                                        <CheckCircleIcon
                                                                            sx={{
                                                                                fontSize: 28,
                                                                                color: palette.fg,
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <PlayCircleFilledIcon
                                                                            sx={{
                                                                                fontSize: 28,
                                                                                color: palette.fg,
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        flexDirection:
                                                                            'column',
                                                                        gap: 0.75,
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: palette.fg,
                                                                            fontWeight: 700,
                                                                            fontSize:
                                                                                '0.875rem',
                                                                            lineHeight: 1.3,
                                                                        }}
                                                                    >
                                                                        {
                                                                            progress.statusLabel
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            fontSize:
                                                                                '0.75rem',
                                                                            lineHeight: 1.3,
                                                                        }}
                                                                    >
                                                                        Stage{' '}
                                                                        {
                                                                            progress.stageInWorkshop
                                                                        }
                                                                        /
                                                                        {
                                                                            progress.stagesInWorkshop
                                                                        }
                                                                    </Typography>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={
                                                                            (progress.stageInWorkshop /
                                                                                progress.stagesInWorkshop) *
                                                                            100
                                                                        }
                                                                        sx={{
                                                                            height: 8,
                                                                            borderRadius: 4,
                                                                            backgroundColor:
                                                                                '#e5e7eb',
                                                                            '& .MuiLinearProgress-bar':
                                                                                {
                                                                                    backgroundColor:
                                                                                        palette.fg,
                                                                                    borderRadius: 4,
                                                                                },
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: 1.5,
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                <RadioButtonUncheckedIcon
                                                                    sx={{
                                                                        fontSize: 28,
                                                                        color: '#d1d5db',
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: '#9ca3af',
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    Not Started
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                            }}
                                                        >
                                                            {date
                                                                ? toTimeDDMonYearFormat(
                                                                      date,
                                                                  )
                                                                : 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    {nextStageButtonEnable && (
                                                        <TableCell>
                                                            {showNextStageButton && (
                                                                <Button
                                                                    variant="text"
                                                                    endIcon={
                                                                        <ArrowForwardIcon />
                                                                    }
                                                                    onClick={
                                                                        setNextStage
                                                                    }
                                                                    disabled={isProceedToNextStageDisabled(
                                                                        'NextStage',
                                                                        workshop.id,
                                                                        step
                                                                            .next
                                                                            ?.id,
                                                                    )}
                                                                    sx={
                                                                        actionButtonSx
                                                                    }
                                                                >
                                                                    Next Stage
                                                                </Button>
                                                            )}
                                                            {showCommenceButton && (
                                                                <Button
                                                                    variant="text"
                                                                    startIcon={
                                                                        <PlayCircleFilledIcon />
                                                                    }
                                                                    onClick={
                                                                        setNextStage
                                                                    }
                                                                    disabled={isProceedToNextStageDisabled(
                                                                        'CommenceWorkshop',
                                                                        workshop.id,
                                                                        step
                                                                            .next
                                                                            ?.id,
                                                                    )}
                                                                    sx={
                                                                        actionButtonSx
                                                                    }
                                                                >
                                                                    Commence
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            </Box>
            <NewPlanDialog
                open={newPlanDialogOpen}
                onClose={handleCloseNewPlanDialog}
                onConfirm={handleNewPlanCreation}
            />
            <UpdatePlanDialog
                open={updatePlanDialogOpen}
                onClose={handleCloseUpdatePlanDialog}
                onSavePlanDetails={handleUpdatePlan}
            />
        </>
    );
};

BFMCDetails.displayName = 'BFMCDetails';
export default BFMCDetails;
